import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../utils/api';
import { PDFDownloadLink } from '@react-pdf/renderer';
import EvaluationDocumentResolver from '../../../Components/Templates/Evaluation/EvaluationDocumentResolver';
import { useAuth } from '../../../context/AuthContext';
import { showToast, showConfirm } from '../../../Components/ToastNotifications';

const EvaluationDetail = () => {
  const { authUser } = useAuth();
  const { batchId = 1 } = authUser?.activeSession || {};
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const {
        status,
        message,
        data: result,
      } = await api.getEvaluationsById({ id });
      if (status !== 'success') {
        setError(message || 'Gagal memuat data evaluasi');
        return;
      }
      setData(result);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Terjadi kesalahan saat memuat data');
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onHandleDeleteStudents = async () => {
    showConfirm({
      message: 'Apakah Anda yakin ingin menghapus mahasiswa ini dari daftar evaluasi?',
      onConfirm: async () => {
        try {
          const { status, message } = await api.deleteStudentsEvaluation({
            id,
            batchId,
          });
          if (status !== 'success') {
            showToast(message, 'danger');
          } else {
            showToast(
              'Mahasiswa berhasil dihapus dari daftar evaluasi.',
              'success',
            );
            navigate('/mentor/mentees/evaluations');
          }
        } catch (err) {
          console.error(err);
          showToast('Terjadi kesalahan saat menghapus mahasiswa.', 'danger');
        }
      },
    });
  };

  const onHandleUpdateNote = async (value) => {
    try {
      await api.updateEvaluationNote({ finalEvaluationNote: value, id });
      setData((prev) => ({ ...prev, finalEvaluationNote: value }));
    } catch (err) {
      console.error('Failed to update note:', err);
    }
  };

  const onHandleRefreshFinalEvaluations = async () => {
    setIsRefreshing(true);
    try {
      await api.refreshFinalEvaluations({ id });
      await fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const onHandleGeneratedFinalEvaluations = async () => {
    setIsRefreshing(true);
    try {
      await api.generateFinalEvaluations({ id });
      await fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsRefreshing(false);
    }
  };

  const evaluationDocument = React.useMemo(
    () => (data ? <EvaluationDocumentResolver data={data} /> : null),
    [data],
  );

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-5'>
        <div
          className='spinner-border text-primary'
          role='status'
        >
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='text-center py-5'>
        <div
          className='bg-danger bg-opacity-10 d-inline-flex align-items-center justify-content-center rounded-circle mb-3'
          style={{ width: '80px', height: '80px' }}
        >
          <i className='bi bi-exclamation-triangle text-danger fs-1'></i>
        </div>
        <h5 className='text-dark fw-bold mb-3'>{error}</h5>
        <button
          onClick={() => navigate('/mentor/mentees/evaluations')}
          className='btn btn-primary px-4 fw-bold'
        >
          <i className='bi bi-arrow-left me-2'></i> Kembali ke Daftar
        </button>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className='container-fluid p-0 pb-5'>
      {/* Premium Header */}
      <div className='card border-0 rounded-4 mb-4 overflow-hidden shadow-sm'>
        <div className='card-body p-4'>
          <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3'>
            <div className='d-flex align-items-center gap-3'>
              <button
                onClick={() => navigate('/mentor/mentees/evaluations')}
                className='btn btn-light rounded-circle d-flex align-items-center justify-content-center border transition-all hover-scale'
                style={{ width: '40px', height: '40px' }}
              >
                <i className='bi bi-arrow-left'></i>
              </button>
              <div>
                <span className='badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold small mb-1'>
                  Detil Evaluasi Mahasiswa
                </span>
                <h1 className='h4 fw-bold mb-0 text-dark'>
                  {data?.mentessFirstName} {data?.mentessLastName}
                </h1>
              </div>
            </div>

            <div className='d-flex gap-2 ms-md-auto mt-3 mt-md-0'>
              <button
                onClick={onHandleDeleteStudents}
                className='btn btn-outline-danger px-3 fw-bold d-flex align-items-center gap-2'
              >
                <i className='bi bi-trash'></i>
                Hapus
              </button>

              <button
                onClick={onHandleRefreshFinalEvaluations}
                disabled={isRefreshing}
                className='btn btn-outline-primary px-3 fw-bold d-flex align-items-center gap-2'
              >
                {isRefreshing ? (
                  <span className='spinner-border spinner-border-sm'></span>
                ) : (
                  <i className='bi bi-arrow-clockwise'></i>
                )}
                Refresh
              </button>

              <PDFDownloadLink
                className='btn btn-primary px-4 fw-bold d-flex align-items-center gap-2 rounded-pill'
                document={evaluationDocument}
                fileName={`EVALUASI_FINAL_${data?.mentessFirstName}_${data?.mentessLastName}.pdf`}
              >
                {({ loading }) =>
                  loading ? (
                    <>
                      <span className='spinner-border spinner-border-sm'></span>{' '}
                      Loading
                    </>
                  ) : (
                    <>
                      <i className='bi bi-download'></i> Download PDF
                    </>
                  )
                }
              </PDFDownloadLink>
            </div>
          </div>
        </div>
      </div>

      <div className='row g-4'>
        {/* Left Column: Academic & Identity */}
        <div className='col-lg-4'>
          <div
            className='d-grid gap-4 sticky-top'
            style={{ top: '1.5rem', zIndex: 10 }}
          >
            {/* User Info Card */}
            <div className='card border-0 rounded-4 shadow-sm'>
              <div className='card-body p-4'>
                <h6 className='fw-bold text-dark mb-4 pb-2 border-bottom'>
                  Profil Mahasiswa
                </h6>
                <div className='row g-3'>
                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-person-badge me-2 text-primary'></i>
                        Mentor Pembimbing
                      </label>
                      <span className='fw-bold text-dark'>
                        {data?.mentors?.firstName} {data?.mentors?.lastName}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-book me-2 text-primary'></i>
                        Jurusan
                      </label>
                      <span className='fw-semibold text-dark small'>
                        {data?.universities?.universitiesMajor || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-mortarboard me-2 text-primary'></i>
                        Universitas
                      </label>
                      <span className='fw-semibold text-dark small'>
                        {data?.universities?.universitiesName || '-'}
                      </span>
                    </div>
                  </div>

                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-light border border-light-subtle h-100'>
                      <label className='small text-muted mb-1 d-block fw-medium'>
                        <i className='bi bi-briefcase me-2 text-primary'></i>
                        Divisi Penempatan
                      </label>
                      <div className='d-block mt-1'>
                        <span className='fw-semibold text-dark small'>
                          {data?.institutions?.divisionName || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Score Summary */}
            <div className='card border-0 rounded-4 bg-primary text-white overflow-hidden shadow'>
              <div className='card-body p-4 position-relative'>
                <i
                  className='bi bi-star-fill position-absolute'
                  style={{
                    right: '1rem',
                    top: '1rem',
                    opacity: 0.2,
                    fontSize: '4rem',
                  }}
                ></i>
                <h6 className='fw-bold mb-4 opacity-75'>Ringkasan Nilai</h6>
                <div className='row g-3'>
                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-white bg-opacity-10 border border-white border-opacity-10 d-flex justify-content-between align-items-center h-100'>
                      <span className='small opacity-75'>Nilai Rata-Rata</span>
                      <span className='h4 fw-bold mb-0'>
                        {data?.finalEvaluationAverage || '0'}
                      </span>
                    </div>
                  </div>
                  <div className='col-12 col-md-6 col-lg-12'>
                    <div className='p-3 rounded-3 bg-white bg-opacity-10 border border-white border-opacity-10 d-flex justify-content-between align-items-center h-100'>
                      <span className='small opacity-75'>Total Akumulasi</span>
                      <span className='h4 fw-bold mb-0'>
                        {data?.finalEvaluationTotal || '0'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Evaluations */}
        <div className='col-lg-8'>
          <div className='d-grid gap-4'>
            {/* Notes Section */}
            <div className='card border-0 rounded-4 shadow-sm overflow-hidden'>
              <div className='card-body p-4'>
                <h6 className='fw-bold text-dark d-flex align-items-center gap-2 mb-3'>
                  <i className='bi bi-pencil-square text-primary'></i>
                  Catatan Evaluasi Final
                </h6>
                <textarea
                  rows={3}
                  className='form-control border-light-subtle bg-light shadow-none'
                  placeholder='Tulis catatan evaluasi di sini...'
                  value={data?.finalEvaluationNote || ''}
                  onChange={(e) =>
                    setData((prev) => ({
                      ...prev,
                      finalEvaluationNote: e.target.value,
                    }))
                  }
                  onBlur={(e) => onHandleUpdateNote(e.target.value)}
                  style={{ fontSize: '0.9rem' }}
                />
                <p
                  className='text-muted'
                  style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}
                >
                  <i className='bi bi-info-circle me-1'></i> Catatan akan
                  tersimpan otomatis saat Anda meninggalkan area input.
                </p>
              </div>
            </div>

            {/* Attendance Score Section — hanya formula v2. Di v1, skor
                kehadiran sudah termasuk dalam perhitungan KPI BCF, jadi
                menampilkannya di sini terpisah cuma bikin duplikat/rancu. */}
            {data?.formulaVersion === 'v2' && (
              <div className='card border-0 rounded-4 shadow-sm'>
                <div className='card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center'>
                  <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                    <i className='bi bi-clock-history text-warning'></i>
                    Komitmen Program (Presensi)
                  </h6>
                </div>
                <div className='card-body p-4'>
                  <div className='row align-items-center g-4'>
                    <div className='col-md-7'>
                      <div className='d-flex justify-content-between small text-muted mb-2'>
                        <span>Skor Kehadiran Valid & Logbook</span>
                        <span className='fw-bold text-dark'>
                          {data?.attendanceMetrics?.score ?? 0} / 100
                        </span>
                      </div>
                      <div
                        className='progress bg-light mb-1'
                        style={{ height: '8px' }}
                      >
                        <div
                          className='progress-bar rounded-pill shadow-none bg-warning'
                          style={{
                            width: (data?.attendanceMetrics?.score ?? 0) + '%',
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className='col-md-5'>
                      <div className='d-grid gap-2'>
                        <div className='d-flex justify-content-between p-2 rounded bg-light border border-light-subtle shadow-xs'>
                          <span className='small text-muted'>Hadir Valid:</span>
                          <span className='fw-bold text-success'>
                            {data?.attendanceMetrics?.totalAttendance ?? 0}{' '}
                            <span className='fw-normal text-muted small'>
                              hari
                            </span>
                          </span>
                        </div>
                        <div className='d-flex justify-content-between p-2 rounded bg-light border border-light-subtle shadow-xs'>
                          <span className='small text-muted'>
                            Total Hari Wajib:
                          </span>
                          <span className='fw-bold text-dark'>
                            {data?.attendanceMetrics?.totalDays ?? 0}{' '}
                            <span className='fw-normal text-muted small'>
                              hari
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='card-footer bg-light border-0 px-4 py-3 border-top'>
                  <div className='d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2'>
                    <div className='text-muted small'>
                      <i className='bi bi-info-circle me-1 text-warning'></i>
                      Kehadiran valid & pengisian logbook berkontribusi 35%
                      terhadap nilai kelulusan.
                    </div>
                    <div className='text-sm-end'>
                      <span className='small text-muted fw-semibold me-2'>
                        Kontribusi Kelulusan:
                      </span>
                      <span className='fw-bold text-success'>
                        +{' '}
                        {((data?.attendanceMetrics?.score ?? 0) * 0.35).toFixed(
                          2,
                        )}{' '}
                        <span
                          className='text-muted fw-normal'
                          style={{ fontSize: '0.75rem' }}
                        >
                          / 35.00
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PAT Section */}
            {data?.patProgressMetrics?.totalUsersDivision > 0 && (
              <div className='card border-0 rounded-4 shadow-sm overflow-hidden'>
                <div className='card-header bg-white border-bottom p-4 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3'>
                  <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                    <i className='bi bi-people-fill text-info'></i>
                    Penilaian Antar Teman (PAT)
                  </h6>
                  <div className='d-flex flex-wrap gap-2'>
                    <button
                      onClick={() =>
                        navigate(`/mentor/mentees/${id}/evaluations/pat/given`)
                      }
                      className='btn btn-outline-secondary btn-sm fw-bold px-3 rounded-pill d-flex align-items-center gap-1'
                      title='Lihat penilaian yang diberikan oleh mahasiswa ini kepada rekannya'
                    >
                      <i className='bi bi-box-arrow-up'></i>
                      PAT Diberikan
                    </button>
                    <button
                      onClick={() =>
                        navigate(
                          `/mentor/mentees/${id}/evaluations/pat/received`,
                        )
                      }
                      className='btn btn-outline-primary btn-sm fw-bold px-3 rounded-pill d-flex align-items-center gap-1'
                      title='Lihat penilaian yang diterima oleh mahasiswa ini dari rekannya'
                    >
                      <i className='bi bi-box-arrow-in-down'></i>
                      PAT Diterima
                    </button>
                  </div>
                </div>
                <div className='card-body p-4'>
                  <div className='row align-items-center g-4'>
                    <div className='col-md-7'>
                      <div className='d-flex justify-content-between small text-muted mb-2'>
                        <span>Progres Penilaian</span>
                        <span className='fw-bold text-dark'>
                          {data?.patProgressMetrics?.progress}%
                        </span>
                      </div>
                      <div
                        className='progress bg-light mb-1'
                        style={{ height: '8px' }}
                      >
                        <div
                          className='progress-bar rounded-pill shadow-none bg-info'
                          style={{
                            width: data?.patProgressMetrics?.progress + '%',
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className='col-md-5'>
                      <div className='d-grid gap-2'>
                        <div className='d-flex justify-content-between p-2 rounded bg-light border border-light-subtle shadow-xs'>
                          <span className='small text-muted'>
                            Sudah Menilai:
                          </span>
                          <span className='fw-bold text-success'>
                            {data?.patProgressMetrics?.hasEvaluated}
                          </span>
                        </div>
                        <div className='d-flex justify-content-between p-2 rounded bg-light border border-light-subtle shadow-xs'>
                          <span className='small text-muted'>
                            Belum Menilai:
                          </span>
                          <span className='fw-bold text-danger'>
                            {data?.patProgressMetrics?.notEvaluated}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='card-footer bg-light border-0 px-4 py-3 border-top'>
                  {data?.patProgressMetrics?.totalUsersDivision > 0 && (
                    <div className='row g-3 small text-muted mb-3 pb-3 border-bottom'>
                      <div className='col-sm-6'>
                        <span className='fw-bold text-dark d-block mb-1'>
                          <i className='bi bi-box-arrow-up text-secondary me-1'></i>
                          PAT Diberikan:
                        </span>
                        Penilaian kolaborasi yang diisi oleh mahasiswa ini untuk
                        menilai performa rekan satu divisinya.
                      </div>
                      <div className='col-sm-6'>
                        <span className='fw-bold text-dark d-block mb-1'>
                          <i className='bi bi-box-arrow-in-down text-primary me-1'></i>
                          PAT Diterima:
                        </span>
                        Nilai masukan dan umpan balik (peer review) dari rekan
                        satu divisi yang diterima oleh mahasiswa ini.
                      </div>
                    </div>
                  )}
                  {data?.formulaVersion === 'v2' ? (
                    <div className='d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2'>
                      <div className='text-muted small'>
                        <i className='bi bi-info-circle me-1 text-info'></i>
                        Evaluasi kolaborasi dan kepemimpinan oleh rekan
                        se-divisi menyumbang 25% bobot kelulusan.
                      </div>
                      <div className='text-sm-end'>
                        <span className='small text-muted fw-semibold me-2'>
                          Kontribusi Kelulusan:
                        </span>
                        <span className='fw-bold text-info'>
                          +{' '}
                          {Math.max(
                            0,
                            (data?.finalEvaluationAverage ?? 0) -
                              (data?.attendanceMetrics?.score ?? 0) * 0.35 -
                              (data?.evaluationsKPI?.length
                                ? data.evaluationsKPI.reduce(
                                    (acc, curr) =>
                                      acc + Number(curr.evaluation || 0),
                                    0,
                                  ) / data.evaluationsKPI.length
                                : 0) *
                                0.4,
                          ).toFixed(2)}{' '}
                          <span
                            className='text-muted fw-normal'
                            style={{ fontSize: '0.75rem' }}
                          >
                            / 25.00
                          </span>
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className='text-muted small'>
                      <i className='bi bi-info-circle me-1 text-muted'></i>
                      Evaluasi 360° yang dihitung secara rata-rata bersama pilar
                      penilaian BCF lainnya.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BCF Evaluations */}
            {data?.evaluationsBCF?.evaluations?.length > 0 && (
              <div className='card border-0 rounded-4 shadow-sm overflow-hidden'>
                <div className='card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center'>
                  <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                    <i className='bi bi-building text-primary'></i>
                    Bakrie Center Foundation
                  </h6>
                </div>
                <div className='card-body p-0'>
                  <div className='table-responsive'>
                    <table className='table table-sm align-middle mb-0'>
                      <thead className='text-muted small'>
                        <tr>
                          <th className='ps-4'>#</th>
                          <th>KPI Classification</th>
                          <th>Nilai</th>
                          <th>Catatan</th>
                          <th className='pe-4'>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.evaluationsBCF?.evaluations?.map(
                          (value, key) => (
                            <tr key={key}>
                              <td className='ps-4 text-muted small'>
                                {key + 1}
                              </td>
                              <td className='small fw-medium text-dark'>
                                {value.classification}
                              </td>
                              <td>
                                <span className='fw-bold text-primary'>
                                  {value.evaluation}
                                </span>
                              </td>
                              <td className='small text-muted'>
                                {value.note || '-'}
                              </td>
                              <td className='pe-4'>
                                {value.classification ===
                                  'Penilaian Antar Teman' && (
                                  <button
                                    onClick={() =>
                                      navigate(
                                        `/mentor/mentees/${id}/evaluations/pat/received`,
                                      )
                                    }
                                    className='btn btn-outline-primary btn-sm rounded-pill fw-bold'
                                  >
                                    Lihat
                                  </button>
                                )}
                              </td>
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Mentors Evaluations (HS/SS) */}
            <div className='card border-0 rounded-4 shadow-sm overflow-hidden'>
              <div className='card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center'>
                <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                  <i className='bi bi-award-fill text-warning'></i>
                  {data?.formulaVersion === 'v2'
                    ? 'Penilaian KPI'
                    : 'Penilaian Mentor (HS & SS)'}
                </h6>
                {!data?.evaluationsHS?.length &&
                  !data?.evaluationsSS?.length && (
                    <button
                      onClick={onHandleGeneratedFinalEvaluations}
                      disabled={isRefreshing}
                      className='btn btn-primary btn-sm px-4 fw-bold rounded-pill'
                    >
                      {isRefreshing ? (
                        <span className='spinner-border spinner-border-sm me-2'></span>
                      ) : (
                        <i className='bi bi-magic me-2'></i>
                      )}
                      Generate
                    </button>
                  )}
              </div>
              <div className='card-body p-0'>
                {/* Hardskill */}
                <div className='p-4 border-bottom'>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <span className='badge bg-info-subtle text-info-emphasis border border-info-subtle px-3 rounded-pill fw-bold small'>
                      {data?.formulaVersion === 'v2' ? 'KPI' : 'KPI Hardskill'}
                    </span>
                    {!(data?.programCode === 'BUMDES') && (
                      <button
                        onClick={() =>
                          navigate(
                            `/mentor/mentees/${id}/evaluations/new/hard-skill`,
                            { state: { data } },
                          )
                        }
                        className='btn btn-primary btn-sm px-3 fw-bold rounded-pill shadow-xs'
                      >
                        {data?.formulaVersion === 'v2'
                          ? 'Nilai KPI'
                          : 'Nilai Hardskill'}
                      </button>
                    )}
                  </div>
                  {data?.evaluationsHS?.length > 0 ? (
                    <div className='table-responsive'>
                      <table className='table table-sm align-middle mb-0'>
                        <thead className='text-muted small'>
                          <tr>
                            <th>Classification</th>
                            <th>Score</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.evaluationsHS.map((v, i) => (
                            <tr key={i}>
                              <td className='small fw-medium'>
                                {v.classification}
                              </td>
                              <td>
                                <span className='fw-bold text-primary'>
                                  {v.evaluation}
                                </span>
                              </td>
                              <td className='small text-muted'>
                                {v.note || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className='alert alert-light border text-center small mb-0'>
                      {data?.formulaVersion === 'v2'
                        ? 'KPI belum dinilai'
                        : 'KPI Hardskill belum dinilai'}
                    </div>
                  )}
                </div>

                {/* Softskill */}
                {data?.evaluationsSS?.length > 0 && (
                  <div className='p-4'>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <span className='badge bg-warning-subtle text-warning-emphasis border border-warning-subtle px-3 rounded-pill fw-bold small'>
                        KPI Softskill
                      </span>
                      <button
                        onClick={() =>
                          navigate(
                            `/mentor/mentees/${id}/evaluations/new/soft-skill`,
                            { state: { data } },
                          )
                        }
                        className='btn btn-primary btn-sm px-3 fw-bold rounded-pill shadow-xs'
                      >
                        Nilai Softskill
                      </button>
                    </div>
                    <div className='table-responsive'>
                      <table className='table table-sm align-middle mb-0'>
                        <thead className='text-muted small'>
                          <tr>
                            <th>Classification</th>
                            <th>Score</th>
                            <th>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.evaluationsSS.map((v, i) => (
                            <tr key={i}>
                              <td className='small fw-medium'>
                                {v.classification}
                              </td>
                              <td>
                                <span className='fw-bold text-primary'>
                                  {v.evaluation}
                                </span>
                              </td>
                              <td className='small text-muted'>
                                {v.note || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
              {data?.formulaVersion === 'v2' && (
                <div className='card-footer bg-light border-0 px-4 py-3 border-top'>
                  <div className='d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2'>
                    <div className='text-muted small'>
                      <i className='bi bi-info-circle me-1 text-success'></i>
                      Pencapaian Key Performance Indicators (KPI) oleh mentor
                      menyumbang 40% bobot kelulusan.
                    </div>
                    <div className='text-sm-end'>
                      <span className='small text-muted fw-semibold me-2'>
                        Kontribusi Kelulusan:
                      </span>
                      <span className='fw-bold text-success'>
                        +{' '}
                        {(
                          (data?.evaluationsKPI?.length
                            ? data.evaluationsKPI.reduce(
                                (acc, curr) =>
                                  acc + Number(curr.evaluation || 0),
                                0,
                              ) / data.evaluationsKPI.length
                            : 0) * 0.4
                        ).toFixed(2)}{' '}
                        <span
                          className='text-muted fw-normal'
                          style={{ fontSize: '0.75rem' }}
                        >
                          / 40.00
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* MSIB Score Section */}
            {data?.formulaVersion !== 'v2' && (
              <div className='card border-0 rounded-4 shadow-sm overflow-hidden'>
                <div className='card-header bg-white border-bottom p-4'>
                  <h6 className='fw-bold text-dark mb-0 d-flex align-items-center gap-2'>
                    <i className='bi bi-bar-chart-line-fill text-success'></i>
                    Akumulasi Nilai MSIB
                  </h6>
                </div>
                <div className='card-body p-0'>
                  {data?.evaluationsKPI?.length > 0 ? (
                    <>
                      <div className='table-responsive'>
                        <table className='table table-sm align-middle mb-0'>
                          <thead className='bg-light text-muted small fw-bold'>
                            <tr>
                              <th className='ps-4 pt-3 pb-3'>KPI MSIB</th>
                              <th className='pt-3 pb-3 text-center'>Nilai</th>
                              <th className='pe-4 pt-3 pb-3'>Catatan</th>
                            </tr>
                          </thead>
                          <tbody>
                            {data.evaluationsKPI.map((v, i) => (
                              <tr key={i}>
                                <td className='ps-4 py-2 small fw-medium'>
                                  {v.classification}
                                </td>
                                <td className='text-center fw-bold text-dark'>
                                  {v.evaluation}
                                </td>
                                <td className='pe-4 small text-muted'>
                                  {v.note || '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className='table-success bg-opacity-10'>
                            <tr>
                              <th className='ps-4 py-3'>Akumulasi (HS + SS)</th>
                              <th className='text-center py-3 fs-5 text-success'>
                                {(
                                  data.evaluationsKPI.reduce(
                                    (acc, curr) =>
                                      acc + Number(curr.evaluation || 0),
                                    0,
                                  ) /
                                  (data.evaluationsKPI.length *
                                    (data?.programCode === 'BUMDES' ? 20 : 1))
                                ).toFixed(2)}
                              </th>
                              <th className='pe-4 py-3 text-end small fw-normal opacity-75 text-success text-uppercase'>
                                Nilai Rata-rata
                              </th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      <div className='card-footer bg-light border-0 px-4 py-3 border-top'>
                        <div className='text-muted small'>
                          <i className='bi bi-info-circle me-1 text-muted'></i>
                          Nilai rata-rata akumulasi kompetensi teknis
                          (hardskill) dan non-teknis (softskill).
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className='p-4 text-center'>
                      <div className='alert alert-light border mb-0 small text-muted'>
                        <i className='bi bi-info-circle me-1'></i> Belum ada
                        penilaian MSIB
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        .hover-scale:hover { transform: scale(1.05); }
        .transition-all { transition: all 0.2s ease; }
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .sticky-top { transition: top 0.3s ease; }
        .table-hover tbody tr:hover { background-color: rgba(var(--bs-primary-rgb), 0.02); }
      `}</style>
    </div>
  );
};

export default EvaluationDetail;
